/**
 * Мониторинг загрузки приложения + авто-восстановление для WebKit (iOS).
 *
 * Отслеживает ключевые вехи:
 *   1. html_loaded     — index.html загружен и выполняется JS
 *   2. react_mounted    — React.createRoot().render() вызван
 *   3. auth_resolved    — статус аутентификации опредёлен (залогинен / нет)
 *   4. ui_rendered      — первый полезный рендер (Login / Dashboard)
 *   5. fully_ready      — все данные загружены, интерфейс полностью готов
 *
 * На WebKit (iOS Safari):
 *   - Через 10 с UI не отрисовался → пробуем fastBootFromToken()
 *   - Через 20 с UI всё ещё пуст → force-перезагрузка с cache-buster
 *
 * Все вехи отправляются в:
 *   - /api/client-diagnostics/boot (через reportBootEvent)
 *   - Sentry breadcrumbs (если Sentry инициализирован)
 */

import { useEffect, useRef } from 'react';
import { reportBootEvent } from '../utils/bootDiagnostics';
import { isWebKit } from '../utils/inAppBrowser';
import { autoReloadWithBudget } from '../utils/reloadGuard';
import useAuthStore from '../store/useAuthStore';

// Пороговые значения для авто-восстановления
const RETRY_MS = 10_000;     // через 10 с пробуем fastBootFromToken
const FORCE_MS = 20_000;     // через 20 с — жёсткая перезагрузка
const CHECK_INTERVAL = 2_000; // проверяем каждые 2 с

/**
 * Проверяет, отрисован ли в #root реальный UI, а не заглушка.
 * Возвращает один из статусов: 'loading' | 'error' | 'ready'
 */
function getRootStatus() {
  var root = document.getElementById('root');
  if (!root) return 'loading';

  var html = root.innerHTML.trim();
  if (!html) return 'loading';

  // Всё ещё грузится (спиннер / загрузочный экран)
  if (root.querySelector('.boot-fallback, .boot-error, .auth-loading-screen')) {
    return 'loading';
  }

  // Есть полезный контент: Layout, Login-форма, кнопки, ссылки
  var hasInteractive = Boolean(
    root.querySelector('button, a, input, select, textarea, nav, form'),
  );
  var hasAppLayout = Boolean(root.querySelector(
    '.app-layout, .login-container, .login-form, .login-card, .bottom-nav, .dashboard-shift-shell',
  ));
  var hasText = (root.innerText || '').trim().length > 10;

  if (hasAppLayout || (hasInteractive && hasText)) return 'ready';

  // Что-то есть, но не похоже на полноценный UI
  return 'loading';
}

export function useAppBootMonitor() {
  var startedAt = useRef(Date.now());
  var retriesRef = useRef(0);
  var monitorRef = useRef(null);

  useEffect(function () {
    // Отмечаем начало мониторинга
    reportBootEvent('boot_monitor_started', {
      webkit: isWebKit(),
      userAgent: (navigator.userAgent || '').slice(0, 120),
    });

    var forceReloaded = false;

    function tick() {
      var ms = Date.now() - startedAt.current;
      var status = getRootStatus();

      if (status === 'ready') {
        // Всё готово — прекращаем мониторинг
        reportBootEvent('app_fully_ready', { ms: ms });
        monitorRef.current = null;
        return;
      }

      // Сообщаем в мониторинг, что UI ещё не готов
      reportBootEvent('boot_not_ready', {
        ms: ms,
        status: status,
        retries: retriesRef.current,
      });

      // На WebKit (iOS) пытаемся восстановиться
      if (isWebKit() && !forceReloaded) {
        // Через 10 с пробуем fastBoot
        if (ms >= RETRY_MS && retriesRef.current < 2) {
          retriesRef.current += 1;
          reportBootEvent('webkit_boot_retry', {
            ms: ms,
            attempt: retriesRef.current,
          });

          try {
            var authState = useAuthStore.getState();
            if (authState.fastBootFromToken()) {
              reportBootEvent('webkit_boot_retry_success', { ms: ms });
              monitorRef.current = null;
              return;
            }
            authState.forceBootstrapExit();
          } catch (e) {
            reportBootEvent('webkit_boot_retry_error', {
              ms: ms,
              error: e?.message,
            });
          }
        }

        // Через 20 с — жёсткая перезагрузка с cache-buster.
        // Через общий бюджет (_boot в URL), чтобы не зациклиться на iOS.
        if (ms >= FORCE_MS && !forceReloaded) {
          forceReloaded = true;
          reportBootEvent('webkit_boot_force_reload', { ms: ms });
          if (!autoReloadWithBudget()) {
            // Бюджет исчерпан — оставляем текущий экран (есть загрузочная
            // заглушка), дальнейшие перезагрузки только усугубили бы цикл.
            reportBootEvent('webkit_boot_force_reload_skipped', { ms: ms, reason: 'budget_exhausted' });
            monitorRef.current = null;
            return;
          }
          return;
        }
      }

      // Продолжаем проверки каждые CHECK_INTERVAL мс
      monitorRef.current = setTimeout(tick, CHECK_INTERVAL);
    }

    // Первая проверка через 3 с (даём React время на монтирование)
    monitorRef.current = setTimeout(tick, 3_000);

    return function () {
      if (monitorRef.current) {
        clearTimeout(monitorRef.current);
        monitorRef.current = null;
      }
    };
  }, []);
}

/**
 * Хелпер: вызывается вручную из компонентов при достижении вех.
 * Пример:
 *   markMilestone('auth_resolved', { authenticated: true })
 *   markMilestone('data_loaded', { section: 'shifts' })
 */
export function markMilestone(name, detail) {
  detail = detail || {};
  reportBootEvent('milestone_' + name, {
    ms: Math.round(performance.now()),
    ...detail,
  });
}
